// Company facts (202610061500): LinkedIn headcount, Google rating and the
// qualification verdict on the company page. One Section: tiles for what
// was observed (with where it came from and when), the fit line from the
// industry rules with its reasons, Refresh and Re-qualify buttons.

const TIER_HUE_F = { premium: 'green', strong: 'teal', qualified: 'blue', does_not_qualify: 'rose', unable: 'slate' };
const TIER_LABEL_F = { premium: 'Premium', strong: 'Strong', qualified: 'Qualified', does_not_qualify: 'Does not qualify', unable: 'Unable to tell' };
const ago = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null);
const PILL_BTN = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' };

// How the size tier was reached (202610082200): the reasoning, the fact that
// disqualified it when there is one, then each tier's signals — seen ones
// with what was observed, the rest folded into one "not seen" line. Shared
// by Facts & fit and the Prospecting drawer. Falls back to the plain reason
// lines for verdicts made before the detail was kept. `hideReasoning`: the
// research drawer (src=tier) shows the reasoning under its own heading.
const SIGNAL_TIER_LABEL = { criteria: 'Criteria', qualified: 'Qualified', strong: 'Strong', premium: 'Premium' };
const LOCATION_TIER_LABEL = { major_metro: 'Major metro', secondary_metro: 'Secondary metro', rural: 'Rural / small market' };
const SizeChecks = ({ detail, reasons, hideReasoning = false }) => {
  const [why, setWhy] = React.useState(false);
  const d = detail || null;
  if (!d || !(d.signals || []).length) {
    return (reasons || []).length > 0 ? (
      <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
        {reasons.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    ) : null;
  }
  const mark = { met: ['✓', 'var(--green-fg)'], not_met: ['✗', 'var(--rose-fg)'] };
  const tiers = ['criteria', 'qualified', 'strong', 'premium'].map((t) => [t, d.signals.filter((x) => x.tier === t)]).filter(([, xs]) => xs.length);
  return (
    <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5, marginTop: 6 }}>
      {d.disqualifier && <div style={{ color: 'var(--rose-fg)', marginBottom: 6 }}>{d.disqualifier}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 12, rowGap: 6 }}>
        {tiers.map(([t, xs]) => {
          const seen = xs.filter((x) => x.status !== 'not_seen');
          const unseen = xs.filter((x) => x.status === 'not_seen');
          return (
            <React.Fragment key={t}>
              <span style={{ color: 'var(--text-4)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, paddingTop: 2 }}>
                {SIGNAL_TIER_LABEL[t]} <span style={{ fontVariantNumeric: 'tabular-nums' }}>{xs.filter((x) => x.status === 'met').length}/{xs.length}</span>
              </span>
              <div style={{ minWidth: 0 }}>
                {seen.map((x, i) => (
                  <div key={i}>
                    <span style={{ color: mark[x.status][1], fontWeight: 600, marginRight: 6 }}>{mark[x.status][0]}</span>
                    <span style={{ color: 'var(--text-2)' }}>{x.signal}</span>
                    {x.observed && <span> — {x.observed}</span>}
                  </div>
                ))}
                {unseen.length > 0 && (
                  <div style={{ color: 'var(--text-4)' }}>Not seen: {unseen.map((x) => x.signal).join(' · ')}</div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {(d.evidence_source || d.data_completeness) && (
        <div style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 6 }}>
          {[d.evidence_source, d.data_completeness ? `${d.data_completeness} data` : null].filter(Boolean).join(' · ')}
        </div>
      )}
      {(d.notes || []).length > 0 && <div style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 6 }}>{d.notes.join(' · ')}</div>}
      {d.reasoning && !hideReasoning && (
        <div style={{ marginTop: 6 }}>
          <button type="button" onClick={() => setWhy((v) => !v)} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: 'var(--accent-text)', fontWeight: 500 }}>
            {why ? '▾' : '▸'} Reasoning
          </button>
          {why && <div style={{ marginTop: 4 }}>{d.reasoning}</div>}
        </div>
      )}
    </div>
  );
};

// `bare` / `readOnly` (record-page redesign step 6, the Company intel drawer):
// the same body under a plain heading with no fold, and no Refresh / Qualify.
// Both default off.
const CompanyFactsSection = ({ companyId, sectionId = 'company.facts', bare = false, readOnly = false }) => {
  const [data, setData] = React.useState(undefined);
  const [busy, setBusy] = React.useState(null);     // 'refresh' | 'qualify' | null
  const load = React.useCallback(() => {
    if (!companyId) return;
    window.AtlasApi.companyFacts(companyId).then(setData).catch(() => setData(null));
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);
  // The research drawer's Re-qualify: re-read so the pills and the checks follow.
  React.useEffect(() => {
    const onChanged = (e) => {
      const d = (e && e.detail) || {};
      const nid = window.RecordPanelsCore.normId;   // case-blind ids; the core loads first
      if (d.src === 'tier' && d.companyId && companyId && nid(d.companyId) === nid(companyId)) load();
    };
    window.addEventListener('atlas-research-changed', onChanged);
    return () => window.removeEventListener('atlas-research-changed', onChanged);
  }, [companyId, load]);
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'refresh') await window.AtlasApi.refreshCompanyFacts(companyId);
      else await window.AtlasApi.qualifyCompany(companyId);
      await load();
      window.pushToast?.({ type: 'success', title: kind === 'refresh' ? 'Facts refreshed' : 'Qualified against the rules' });
    } catch (e) {
      window.pushToast?.({ type: 'error', title: kind === 'refresh' ? 'Could not refresh' : 'Could not qualify', body: e.message });
    } finally { setBusy(null); }
  };
  if (data === undefined) return null;
  const o = (data && data.observables) || null;
  const q = (data && data.qualification) || null;
  const cfg = (data && data.configured) || {};
  // The tier and fit pills open how the verdict was reached (?panel=research&src=tier).
  const why = !!(window.researchDrawerHere && window.researchDrawerHere());
  const whyBtn = (pill) => (why
    ? <button type="button" onClick={() => window.openResearchRun({ src: 'tier' })} title="How it got here" style={PILL_BTN}>{pill}</button>
    : pill);
  const tile = (label, value, sub, href) => (
    <div style={{ minWidth: 130, flex: 1 }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
        {href && value !== '—' ? <a href={href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>{value}</a> : value}
      </div>
      {sub && <div style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{sub}</div>}
    </div>
  );
  const preview = [
    o && o.linkedin_headcount ? `${o.linkedin_headcount.toLocaleString()} on LinkedIn` : null,
    o && o.google_rating != null ? `${o.google_rating}★ · ${(o.google_reviews || 0).toLocaleString()} reviews` : null,
    q && q.size_tier ? TIER_LABEL_F[q.size_tier] : null,
  ].filter(Boolean).join(' · ') || 'not checked yet';
  const action = readOnly ? null : (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <window.Btn size="xs" variant="ghost" disabled={!!busy || (!cfg.apollo && !cfg.google)} title={!cfg.apollo && !cfg.google ? 'Set APOLLO_KEY and OUTSCRAPER_API_KEY on the API' : 'Look up LinkedIn headcount and Google rating again'} onClick={() => act('refresh')}>{busy === 'refresh' ? 'Refreshing…' : (o ? 'Refresh' : 'Look up')}</window.Btn>
            <window.Btn size="xs" variant="ghost" disabled={!!busy} title="Run the industry rules on this company" onClick={() => act('qualify')}>{busy === 'qualify' ? 'Qualifying…' : (q ? 'Re-qualify' : 'Qualify')}</window.Btn>
          </span>
  );
  const body = (
        <div style={{ padding: bare ? '2px 0 12px' : '12px 14px 14px' }}>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          {tile('LinkedIn employees', o && o.linkedin_headcount ? o.linkedin_headcount.toLocaleString() : '—',
            o && o.apollo_checked_at ? `Apollo · ${ago(o.apollo_checked_at)}` : (cfg.apollo ? 'not looked up' : 'Apollo key not set'), o && o.linkedin_url)}
          {tile('Google rating', o && o.google_rating != null ? `${o.google_rating}★` : '—',
            o && o.google_checked_at ? `${(o.google_reviews || 0).toLocaleString()} reviews${o.google_match === 'name' ? ' · matched by name only' : ''} · ${ago(o.google_checked_at)}` : (cfg.google ? (o && o.google_checked_at === null && o.apollo_checked_at ? 'no listing found' : 'not looked up') : 'Outscraper key not set'),
            o && o.google_maps_url)}
          {tile('Founded', o && o.founded_year ? String(o.founded_year) : '—', o && o.founded_year ? 'Apollo' : null)}
          {tile('HQ (Apollo)', o && (o.hq_city || o.hq_state) ? [o.hq_city, o.hq_state].filter(Boolean).join(', ') : '—', o && o.phone ? o.phone : null)}
          {/* Who Apollo says owns it — only when it names someone. The
              acquisition record this files shows up as "Acquired? · confirm"
              in the header; this tile is the fact behind it. */}
          {o && o.apollo_owned_by_name && tile('Owner (Apollo)', o.apollo_owned_by_name,
            o.apollo_funding_stage && /merger/i.test(o.apollo_funding_stage) && o.apollo_funding_date
              ? `acquired ${String(o.apollo_funding_date).slice(0, 4)}`
              : (o.apollo_parent_name && o.apollo_parent_name !== o.apollo_owned_by_name ? `parent ${o.apollo_parent_name}` : 'per Apollo'),
            o.apollo_owned_by_domain ? `https://${o.apollo_owned_by_domain}` : null)}
          {o && o.apollo_public_symbol && tile('Ticker', o.apollo_public_symbol, 'publicly traded · Apollo')}
        </div>
        {o && o.apollo_revenue_est != null && (
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 8 }}>Apollo estimates revenue at {window.fmtMoney(o.apollo_revenue_est)} — kept for the record, never used to qualify.</div>
        )}
        {q && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10 }}>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
                {whyBtn(<window.Pill hue={TIER_HUE_F[q.size_tier] || 'slate'} sz="sm">{TIER_LABEL_F[q.size_tier] || q.size_tier || '—'}</window.Pill>)}
                <span><b>{q.industry_name || q.industry_proposed || 'no industry'}</b>{q.industry_confidence != null ? <span style={{ color: 'var(--text-4)' }}> · {Math.round(q.industry_confidence * 100)}%</span> : null}</span>
                {q.geo_fit && whyBtn(<window.Pill hue={q.geo_fit === 'yes' ? 'green' : q.geo_fit === 'no' ? 'rose' : 'slate'} sz="sm" title={q.geo_reason || ''}>geo {q.geo_fit}</window.Pill>)}
                {q.size_detail && LOCATION_TIER_LABEL[q.size_detail.location_tier] && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{LOCATION_TIER_LABEL[q.size_detail.location_tier]}</span>}
                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{q.size_detail && q.size_detail.confidence ? `${q.size_detail.confidence} confidence · ` : ''}{q.source} · {ago(q.qualified_at)}</span>
              </div>
              {q.industry_reason && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>{q.industry_reason}</div>}
              <SizeChecks detail={q.size_detail} reasons={q.size_reasons} />
            </div>
        </div>
        )}
        </div>
  );
  // bare: the Company intel drawer (views/company_intel.jsx): the same body under a plain heading, no fold.
  if (bare) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px', minWidth: 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>Facts &amp; fit</h3>
          <div style={{ marginLeft: 'auto', minWidth: 0 }}>{action}</div>
        </div>
        {body}
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <window.Section
        title="Facts & fit"
        collapsible id={sectionId} defaultOpen
        preview={preview}
        action={action}
      >
        {body}
      </window.Section>
    </div>
  );
};

Object.assign(window, { CompanyFactsSection, SizeChecks, LOCATION_TIER_LABEL });
// The research drawer's tier adapter (research.jsx) names the tier the same way.
window.COMPANY_TIER = { label: TIER_LABEL_F, hue: TIER_HUE_F };
